import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRowId } from '@mui/x-data-grid';
import { Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import { useApi } from '../../../../services/HttpService';
import ScrollableDataGrid from '../../../../components/tables/ScrollableDataGrid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import BaseModal from '../../../../components/modals/BaseModal';
import { StickyAlert } from '../../../../components/ui/StickyAlert';
import { highlightedGridColumns } from '../../../../components/tables/BaseTable';
import { Member, newMember } from '../../../../model';
import { EnhancedTableToolbar } from '../../../../components/tables/AdminTable';
import HardDeleteDialog from '../../../../components/modals/dialog/HardDeleteDialog';
import { ContactOverview } from './ContactOverview';

let defaultColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'member_number', headerName: 'Medlemsnummer', width: 130 },
  { field: 'first_name', headerName: 'Fornavn', width: 130 },
  { field: 'last_name', headerName: 'Etternavn', width: 130 },
  { field: 'name', headerName: 'Fullt navn', width: 160 },
  { field: 'emailaddress', headerName: 'e-post', width: 250 },
  { field: 'telephone', headerName: 'Telefon', width: 150 },
  { field: 'address', headerName: 'Adresse', width: 250 },
  { field: 'member_type_names', headerName: 'Medlemstyper', width: 200 },
  { field: 'organization_names', headerName: 'Organisasjoner', width: 250 },
  { field: 'company_names', headerName: 'Bedrifter', width: 250 },
];

export const ManageContacts = () => {
  const theme = useTheme();
  const { api, isLoading, error } = useApi();
  const { api: deleteApi, isLoading: deleteIsLoading, error: deleteError } = useApi();
  const { api: dataApi, error: dataError } = useApi();
  const [items, setItems] = useState<Member[]>([]);
  const [selectedItem, setSelectedItem] = useState<Member>(newMember);
  const [filteredItems, setFilteredItems] = useState<Member[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly GridRowId[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditItem, setOpenEditItem] = useState(false);

  const apiRef = useRef<GridApiCommunity>(null);

  // Use these to send the column width and visibility conf for a table to the server. Load and update them when component mounts
  const [mainColumns, setMainColumns] = useState<GridColDef[]>(defaultColumns);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    getItems();
  }, []);

  // React.useEffect(() => {
  //   setTimeout(() => {
  //     let cols = defaultColumns.map(col => {
  //       if (col.field === "first_name") {
  //         col.width = 250
  //       }
  //       return col
  //     });
  //     setMainColumns(cols)
  //   }, 2000)
  // }, []);

  const getItems = () => {
    const body = {
      table_name: "contact",
      search: searchText,
      limit: 0,
      offset: 0,
    }

    api("/manage/tableOverviewQuery", "POST", body).then((res) => {
      if (res?.statusCode === 200) {
        setItems(res.data);
      }
    });
  }

  useEffect(() => {
    handleSearch(searchText);
  }, [items]);

  const handleSearch = (searchText: string) => {
    const query = searchText.toLowerCase();
    setSearchText(query);

    const filteredData = items ? items.filter((row) =>
      mainColumns.some((column) => {

        // Use valueGetter if defined, otherwise use the field directly
        const value = column.valueGetter
          ? column.valueGetter(undefined as never, row, column, apiRef)
          : row[column.field];

        return String(value).toLowerCase().includes(query);
      })
    ) : [];

    setFilteredItems(filteredData);
  };

  const getPageItems = (page: number, pageSize: number) => {
    if (items.length === 0) {
      return [];
    }
    const sublist = items.slice(page * pageSize, (page + 1) * pageSize);
    console.log("returning contacts from index", page * pageSize, "-", (page + 1) * pageSize, "count", sublist.length)
    return sublist;
  }

  const onItemChange = (changedItem: Member) => {
    setItems((prevItems) => {
      const index = prevItems.findIndex(u => u.id === changedItem.id);
      if (index > -1) {
        // the additional text fields are not present, not updating (sorry)
        // return prevItems.map((item, i) => (i === index ? changedItem : item));
        return prevItems;
      } else {
        return [...prevItems, changedItem];
      }
    });

    setSelectedItem(changedItem);
    setSelectedIds([changedItem.id]);
  }

  const handleRowSelectionModelChange = (ids: number[]) => {
    setSelectedIds(ids);

    if (ids.length > 0) {
      let selectedItem = items.find(u => u.id === ids[0]);
      setSelectedItem(selectedItem);
    } else {
      setSelectedItem(newMember());
    }
  }

  const handleRowDoubleClick = (row) => {
    let ids = [row.id];
    handleRowSelectionModelChange(ids);
    setOpenEditItem(true);
  }

  const handleDelete = (hardDelete: boolean) => {
    alert("delete not implemented");
  }

  return (
    <>
      <BaseModal
        open={openEditItem}
        title={"Kontakt"}
        onClose={() => { setOpenEditItem(false) }}
        closeOnBackdropClick={false}
        showButtons={false}
      >
        <ContactOverview item={selectedItem} onClose={() => setOpenEditItem(false)} onItemChange={onItemChange} />
      </BaseModal>
      <HardDeleteDialog
        open={openDelete}
        bodyText={`Er du sikker på at du vil slette ${selectedItem.name}? Trykk på "SLETT" for å bekrefte.`}
        includeHardDelete={true}
        loading={deleteIsLoading}
        errorMessage={deleteError}
        onClose={() => setOpenDelete(false)}
        onConfirm={(hardDelete) => handleDelete(hardDelete)}
      />

      <StickyAlert message={error ? error : dataError ? dataError : null} severity="error" />

      <Box sx={{ m: 1, mt: 3, p: { xs: 1, md: 3 }, pt: 0, width: "100%" }}>
        <Paper elevation={3} sx={{ borderRadius: "4px 4px 0 0" }}>
          <EnhancedTableToolbar
            title={"Contacts"}
            numSelected={openEditItem || selectedIds.length > 0 ? 1 : 0}
            selectTitle={selectedItem.id ? `${selectedItem.name}` : "New contact"}
            onAdd={() => { setSelectedItem(newMember()); setSelectedIds([]); setOpenEditItem(true) }}
            onEdit={() => setOpenEditItem(true)}
            onCancel={() => { setOpenEditItem(false); setSelectedIds([]); setSelectedItem(newMember()) }}
            onDelete={() => setOpenDelete(true)}
            onSearch={handleSearch}
            onRefresh={getItems}
          />
        </Paper>
        {/* {openEditItem ?
          <>
            <ItemOverview item={item} onClose={() => setOpenEditItem(false)} />
          </> : */}
        <ScrollableDataGrid
          height={700}
          rows={filteredItems}
          columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
          // columnVisibilityModel={columnVisibility}
          loading={isLoading}
          onPageRequest={getPageItems}
          checkboxSelection
          disableMultipleRowSelection
          onRowSelectionModelChange={(ids) => handleRowSelectionModelChange(ids)}
          onRowDoubleClick={(row) => handleRowDoubleClick(row)}
          rowSelectionModel={selectedIds}
          keepNonExistentRowsSelected={true}
        />
        {/* } */}
      </Box>
    </>
  )
}